// Boids core from the flock widget: three rules (stay close, fly the same way, give space),
// same coefficients and speed limits. Pure and seeded so it can be tested headless.

import type { Rng } from '@kit/rng';

export interface Bird { x: number; y: number; vx: number; vy: number }
export interface Threat { x: number; y: number; r: number }
export interface Flock { w: number; h: number; B: Bird[] }
export interface Rules { kC: number; kA: number; kS: number }   // 0..10 each, 5 is the tuned default

export const VIS = 50, SEP = 18, VMAX = 2.6, VMIN = 1.4;
export const DEFAULT_RULES: Rules = { kC: 5, kA: 5, kS: 5 };

export function wrapD(d: number, m: number): number { if (d > m / 2) return d - m; if (d < -m / 2) return d + m; return d; }

export function makeFlock(n: number, w: number, h: number, rng: Rng): Flock {
  const B: Bird[] = [];
  for (let i = 0; i < n; i++) { const a = rng() * Math.PI * 2; B.push({ x: rng() * w, y: rng() * h, vx: Math.cos(a) * 2, vy: Math.sin(a) * 2 }); }
  return { w, h, B };
}

export function setCount(f: Flock, n: number, rng: Rng) {
  while (f.B.length < n) { const a = rng() * Math.PI * 2; f.B.push({ x: rng() * f.w, y: rng() * f.h, vx: Math.cos(a) * 2, vy: Math.sin(a) * 2 }); }
  if (f.B.length > n) f.B.length = n;
}

/** one tick; threats push birds away and raise the speed cap like the widget's scare and hawk */
export function stepFlock(f: Flock, rules: Rules, rng: Rng, threats: Threat[] = []) {
  const { w, h, B } = f;
  const N = B.length;
  const ax = new Float32Array(N), ay = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const b = B[i];
    let cx = 0, cy = 0, avx = 0, avy = 0, sx = 0, sy = 0, n = 0;
    for (let j = 0; j < N; j++) {
      if (i === j) continue;
      const o = B[j], dx = wrapD(o.x - b.x, w), dy = wrapD(o.y - b.y, h), d2 = dx * dx + dy * dy;
      if (d2 < VIS * VIS) {
        n++; cx += dx; cy += dy; avx += o.vx; avy += o.vy;
        if (d2 < SEP * SEP && d2 > 0) { const d = Math.sqrt(d2); sx -= dx / d * (SEP - d) / SEP; sy -= dy / d * (SEP - d) / SEP; }
      }
    }
    if (n) { ax[i] += cx / n * 0.0009 * rules.kC + (avx / n - b.vx) * 0.012 * rules.kA; ay[i] += cy / n * 0.0009 * rules.kC + (avy / n - b.vy) * 0.012 * rules.kA; }
    ax[i] += sx * 0.09 * rules.kS; ay[i] += sy * 0.09 * rules.kS;
    for (const t of threats) {
      const dx = wrapD(b.x - t.x, w), dy = wrapD(b.y - t.y, h), d = Math.sqrt(dx * dx + dy * dy);
      if (d < t.r && d > 0) { const fo = (t.r - d) / t.r * 0.9; ax[i] += dx / d * fo; ay[i] += dy / d * fo; }
    }
    ax[i] += (rng() - 0.5) * 0.05; ay[i] += (rng() - 0.5) * 0.05;
  }
  const top = threats.length ? VMAX * 1.5 : VMAX;
  for (let i = 0; i < N; i++) {
    const b = B[i];
    b.vx += ax[i]; b.vy += ay[i];
    const s = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
    if (s > top) { b.vx *= top / s; b.vy *= top / s; } else if (s < VMIN && s > 0) { b.vx *= VMIN / s; b.vy *= VMIN / s; }
    b.x = (b.x + b.vx + w) % w; b.y = (b.y + b.vy + h) % h;
  }
}

/** mean distance from each bird to its nearest neighbor (wrap-aware) */
export function meanNearest(f: Flock): number {
  const { w, h, B } = f;
  if (B.length < 2) return 0;
  let sum = 0;
  for (let i = 0; i < B.length; i++) {
    let best = Infinity;
    for (let j = 0; j < B.length; j++) {
      if (i === j) continue;
      const dx = wrapD(B[j].x - B[i].x, w), dy = wrapD(B[j].y - B[i].y, h), d2 = dx * dx + dy * dy;
      if (d2 < best) best = d2;
    }
    sum += Math.sqrt(best);
  }
  return sum / B.length;
}

/** 0 = every bird its own way, 1 = all flying the same way */
export function alignment(f: Flock): number {
  let vx = 0, vy = 0, sp = 0;
  for (const b of f.B) { const s = Math.hypot(b.vx, b.vy) || 1; vx += b.vx / s; vy += b.vy / s; sp += 1; }
  return sp ? Math.hypot(vx, vy) / sp : 0;
}
